import { useState, useEffect } from "react";
import {
  Bell,
  FileText,
  Pin,
  Calendar,
  ChevronRight,
  UserX,
} from "lucide-react";

const NoticeBoardCard = ({
  notices = [],
  circulars = [],
  leaveData = [],
  className = "",
  onViewAll = () => {},
  onNoticeClick = () => {},
  onCircularClick = () => {},
  onLeaveClick = () => {},
}) => {
  const [activeTab, setActiveTab] = useState("notices");

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "text-red-600";
      case "medium":
        return "text-amber-600";
      case "low":
        return "text-green-600";
      default:
        return "text-gray-500";
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "policy":
        return "text-blue-600";
      case "review":
        return "text-purple-600";
      case "security":
        return "text-red-600";
      default:
        return "text-gray-500";
    }
  };

  const getLeaveStatusColor = (status) => {
    switch (status) {
      case "APPROVED":
        return "bg-green-100 text-green-700";
      case "REQUESTED":
        return "bg-yellow-100 text-yellow-700";
      case "REJECTED":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString("en-GB", { month: "short", day: "numeric" });
  };

  const tabs = [
    { id: "notices", label: "Notices", icon: Bell, count: notices.length },
    {
      id: "circulars",
      label: "Circulars",
      icon: FileText,
      count: circulars.length,
    },
    { id: "leaves", label: "On Leave", icon: UserX, count: leaveData.length },
  ];

  const renderLeaveContent = () => {
    if (leaveData.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 pt-2">
          <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center mb-3">
            <UserX className="w-6 h-6 text-gray-400" />
          </div>
          <h3 className="font-semibold text-xl text-gray-700">
            No Leaves Today
          </h3>
          <p className="text-xs">Everyone is present today!</p>
        </div>
      );
    }

    return (
      <div className="space-y-1">
        {leaveData.slice(0, 5).map((leave) => (
          <div
            key={leave.leaveId}
            className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors duration-150 group"
            onClick={() => onLeaveClick(leave)}
          >
            {/* Avatar */}
            <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-xs font-semibold text-blue-600">
                {leave.employeeName?.charAt(0).toUpperCase() || "?"}
              </span>
            </div>

            {/* Main Content */}
            <div className="flex-grow min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-gray-800 truncate">
                  {leave.employeeName}
                </p>
                <span
                  className={`px-1.5 py-0.5 text-[10px] rounded-full font-medium ${getLeaveStatusColor(
                    leave.leaveStatus,
                  )}`}
                >
                  {leave.leaveStatus}
                </span>
              </div>
              <p className="text-xs text-gray-500 truncate">
                {leave.role} • {leave.department}
              </p>
              {leave.fallbackEmployeeName && (
                <p className="text-xs text-blue-600 truncate">
                  Backup: {leave.fallbackEmployeeName}
                </p>
              )}
            </div>

            {/* Leave Duration */}
            <div className="flex-shrink-0">
              <div className="flex items-center text-xs text-gray-500">
                <Calendar className="w-3 h-3 mr-1" />
                <span>{leave.dates?.length || 1}d</span>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        ))}
      </div>
    );
  };

  const renderContent = () => {
    if (activeTab === "leaves") {
      return renderLeaveContent();
    }

    const isNoticeTab = activeTab === "notices";
    const items = isNoticeTab ? notices : circulars;
    const clickHandler = isNoticeTab ? onNoticeClick : onCircularClick;

    if (items.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 pt-2">
          <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center mb-3">
            {isNoticeTab ? (
              <Bell className="w-6 h-6 text-gray-400" />
            ) : (
              <FileText className="w-6 h-6 text-gray-400" />
            )}
          </div>
          <h3 className="font-semibold text-xl text-gray-700">
            No {isNoticeTab ? "Notices" : "Circulars"}
          </h3>
          <p className="text-xs">New items will appear here.</p>
        </div>
      );
    }

    return (
      <div className="space-y-1">
        {items.slice(0, 5).map((item) => (
          <div
            key={item.id}
            className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors duration-150 group"
            onClick={() => clickHandler(item)}
          >
            {/* Unread Indicator */}
            <div
              className={`flex-shrink-0 w-2 h-2 rounded-full ${
                !item.isRead ? "bg-blue-500" : "bg-transparent"
              }`}
            ></div>

            {/* Main Content */}
            <div className="flex-grow min-w-0">
              <div className="flex items-center">
                {item.isPinned && (
                  <Pin className="w-3 h-3 text-blue-500 mr-1.5 flex-shrink-0" />
                )}
                <p className="text-sm font-semibold text-gray-800 truncate">
                  {item.title}
                </p>
              </div>
              <p className="text-xs">
                {isNoticeTab ? (
                  <span className={getPriorityColor(item.priority)}>
                    Priority: {item.priority}
                  </span>
                ) : (
                  <span className={getCategoryColor(item.category)}>
                    Category: {item.category}
                  </span>
                )}
              </p>
            </div>

            {/* Date and Arrow */}
            <div className="text-xs text-gray-500 flex-shrink-0">
              {formatDate(item.date)}
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      className={`bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col h-full ${className}`}
    >
      {/* Header with Tabs */}
      <div className="p-2 border-b border-gray-200 flex-shrink-0">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-full">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center px-2 py-1 rounded-full text-xs font-semibold transition-all duration-200 ${
                  activeTab === tab.id
                    ? "bg-white shadow-sm text-gray-900"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                <Icon className="w-3.5 h-3.5 mr-1.5" />
                <span>{tab.label}</span>
                {tab.count > 0 && (
                  <span
                    className={`ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full ${
                      activeTab === tab.id
                        ? "bg-gray-100 text-gray-500"
                        : "bg-gray-200 text-gray-400"
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content Area */}
      <div className="p-2 flex-grow overflow-y-auto">{renderContent()}</div>
    </div>
  );
};

export default NoticeBoardCard;
